import { useCallback } from 'react'

import { google, outlook, ics } from 'calendar-link'
import { useNavigate } from 'react-router-dom'

import {
  Event,
  RecurringEventRegistrationMode,
  RecurringEventUpdateMode,
} from '@tribeplatform/gql-client/types'
import {
  useCancelEventRegistration,
  useDeleteEvent,
  useGetCalendarInvite,
  useRegisterForEvent,
} from '@tribeplatform/react-sdk/hooks'
import { confirm } from '@tribeplatform/react-ui-kit/Dialog'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

import { useI18n } from '../../i18n/index.js'
import { CalendarProvider } from '../types.js'

export const useEventActions = () => {
  const { getCalendarInvite } = useGetCalendarInvite()
  const { mutateAsync: register, isLoading: isRegistering } =
    useRegisterForEvent()
  const { mutateAsync: cancelRegistration, isLoading: isCancelling } =
    useCancelEventRegistration()
  const { mutateAsync: deleteEventMutation, isLoading: isDeleting } =
    useDeleteEvent()
  const { $t } = useI18n()
  const navigate = useNavigate()

  const registerForEvent = useCallback(
    async (eventId: string, mode?: RecurringEventRegistrationMode) => {
      try {
        await register({
          eventId,
          input: {
            recurringEventRegistrationMode: mode,
          },
        })
      } catch {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Event.Actions.Register.Error.Description',
            defaultMessage: 'Failed to register for event',
          }),
          status: 'error',
        })
      }
    },
    [register, $t],
  )

  const cancelEventRegistration = useCallback(
    async (eventId: string, mode?: RecurringEventRegistrationMode) => {
      const confirmed = await confirm({
        title: $t({
          id: 'Event.Actions.Cancel.Confirmation.Title',
          defaultMessage: 'Cancel your spot for this event?',
        }),
        danger: true,
        cancelLabel: $t({
          id: 'Event.Actions.Cancel.Confirmation.ProceedLabel',
          defaultMessage: 'Keep my spot',
        }),
        proceedLabel: $t({
          id: 'Event.Actions.Cancel.Confirmation.CancelLabel',
          defaultMessage: 'Cancel registration',
        }),
      })
      if (!confirmed) {
        return
      }

      try {
        await cancelRegistration({
          eventId,
          input: { recurringEventRegistrationMode: mode },
        })
      } catch {
        toast({
          title: $t({
            id: 'Generics.Error',
            defaultMessage: 'Error',
          }),
          description: $t({
            id: 'Event.Actions.Cancel.Error.Description',
            defaultMessage: 'Failed to cancel registration',
          }),
          status: 'error',
        })
      }
    },
    [cancelRegistration, $t],
  )

  const deleteEvent = useCallback(
    async (event: Event, mode: RecurringEventUpdateMode) => {
      try {
        await deleteEventMutation({
          eventId: event.id,
          input: { recurringEventUpdateMode: mode },
        })

        toast({
          title: $t({
            defaultMessage: 'Event deleted',
            id: 'Event.HeaderActions.DeleteSuccess',
          }),
          description: $t({
            defaultMessage: 'The event has been deleted successfully',
            id: 'Event.HeaderActions.DeleteSuccess.Description',
          }),
          status: 'success',
        })

        navigate(`${event.space?.relativeUrl}`)
      } catch {
        toast({
          title: $t({
            defaultMessage: 'Error',
            id: 'Event.HeaderActions.DeleteError',
          }),
          description: $t({
            defaultMessage: 'Failed to delete the event',
            id: 'Event.HeaderActions.DeleteError.Description',
          }),
          status: 'error',
        })
      }
    },
    [$t, deleteEventMutation, navigate],
  )

  const handleDeleteEvent = useCallback(
    async (event: Event, mode: RecurringEventUpdateMode) => {
      const confirmed = await confirm({
        title: $t({
          defaultMessage: 'Are you sure you want to delete?',
          id: 'Event.Actions.Delete.Confirmation.Title',
        }),
        description: $t({
          defaultMessage: 'This action cannot be undone.',
          id: 'Event.Actions.Delete.Confirmation.Description',
        }),
        proceedLabel: $t({
          defaultMessage: 'Delete',
          id: 'Generics.Delete',
        }),
        cancelLabel: $t({
          defaultMessage: 'Cancel',
          id: 'Generics.Cancel',
        }),
        danger: true,
      })
      if (!confirmed) {
        return
      }

      await deleteEvent(event, mode)
    },
    [deleteEvent, $t],
  )

  const getCalendarInviteData = useCallback(
    async (eventId: string) => {
      try {
        const calendarInvite = await getCalendarInvite(eventId)

        return {
          uid: calendarInvite.uid,
          title: calendarInvite.title,
          start: calendarInvite.start,
          end: calendarInvite.end,
          description: calendarInvite.description,
          location: calendarInvite.location,
          url: calendarInvite.url,
          organizer: {
            name: calendarInvite.organizerName,
            email: calendarInvite.organizerName,
          },
          rRule: calendarInvite.recurrenceRule,
        }
      } catch {
        toast({
          title: $t({
            defaultMessage: 'Error',
            id: 'Generics.Error',
          }),
          description: $t({
            defaultMessage: 'Failed to get calendar invite data',
            id: 'Event.Actions.GetCalendarInviteData.Error.Description',
          }),
          status: 'error',
        })
        return null
      }
    },
    [getCalendarInvite, $t],
  )

  const addToCalendar = useCallback(
    async (eventId: string, provider: CalendarProvider) => {
      const calendarInviteData = await getCalendarInviteData(eventId)
      if (!calendarInviteData) {
        return
      }

      switch (provider) {
        case CalendarProvider.Google:
          window.open(google(calendarInviteData), '_blank')
          break
        case CalendarProvider.Outlook:
          window.open(outlook(calendarInviteData), '_blank')
          break
        case CalendarProvider.Apple:
          window.open(ics(calendarInviteData), '_blank')
          break
        default: {
          const _unknown: never = provider
          break
        }
      }
    },
    [getCalendarInviteData],
  )

  const downloadICSFile = useCallback(
    async (eventId: string) => {
      const calendarInviteData = await getCalendarInviteData(eventId)
      if (!calendarInviteData) {
        return
      }
      const a = document.createElement('a')
      document.body.appendChild(a)
      a.download = calendarInviteData.title
      a.href = ics(calendarInviteData)
      a.click()
    },
    [getCalendarInviteData],
  )

  return {
    registerForEvent,
    cancelEventRegistration,
    isRegistering,
    isCancelling,
    addToCalendar,
    handleDeleteEvent,
    isDeleting,
    downloadICSFile,
  }
}
